import { useEffect, useState, createRef, useRef } from "react";
import {
  DownloadOutlined,
  SearchOutlined,
  DownOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  Button,
  Table,
  Select,
  Input,
  Space,
  Form,
  Menu,
  Dropdown,
  Tag,
  AutoComplete,
} from "antd";

import { formatPhoneNumber, sortArrayByKey } from "utils/helpers";
import {
  formatName,
  HmoTableProps,
  PatientRecord,
  AWVMemberType,
} from "./utils";
import { ErrorResponse } from "utils/types";
import notifier from "utils/notifier";

import styles from "./HmoTable.module.scss";
import {
  RefreshMemberAwvStatusModel,
  MemberPafModel,
  MemberExcelStatusModel,
} from "store/app/hmo/actions";

import { AWVStatusFilter, AWVStatus, AWVMemberTypeFilter } from "../HMO/utils";
import { debounce } from "lodash";

const HmoTable = (props: HmoTableProps) => {
  const [form] = Form.useForm();
  const DEFAULT_PAGE_NUM = 1;
  const DEFAULT_PAGE_SIZE = 10;
  const { Option } = Select;
  const [filterDataSource, setFilterDataSource] = useState<any[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedIPA, setSelectedIPA] = useState("");
  const [selectedMemberMpi, setSelectedMemberMpi] = useState("");
  const [providerId, setProviderId] = useState("");
  const [providerKeyId, setProviderKeyId] = useState("");
  const [providerList, setProviderList] = useState<any>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<PatientRecord[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUM);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const [selectedMemberName, setSelectedMemberName] = useState("");

  let memberSearchText = useRef("");
  const USERID = sessionStorage.getItem("user_id");

  const isFilterSelected = (filter: any) => {
    if (filter === "All") {
      return selectedValue === undefined;
    }
    return selectedFilters.includes(filter);
  };

  const handleFilterToggle = (toggledFilter: any) => {
    // If 'All' is selected, clear any other selections and fetch data without filters.
    if (toggledFilter === "All") {
      setSelectedFilters([]);
      setSelectedValue(undefined);
      getHMOData(DEFAULT_PAGE_NUM, DEFAULT_PAGE_SIZE, false, []);
    } else {
      if (selectedFilters.includes(toggledFilter)) {
        // Remove the toggled filter if it's already selected.
        const newFilters = selectedFilters.filter((f) => f !== toggledFilter);
        setSelectedFilters(newFilters);
        getHMOData(DEFAULT_PAGE_NUM, DEFAULT_PAGE_SIZE, false, newFilters);
      } else {
        let filteredExistingFilters = selectedFilters;

        if (
          (Object.values(AWVMemberTypeFilter) as string[]).includes(
            toggledFilter
          )
        ) {
          filteredExistingFilters = selectedFilters.filter(
            (filter) =>
              !(Object.values(AWVMemberTypeFilter) as string[]).includes(filter)
          );
        } else {
          filteredExistingFilters = selectedFilters.filter(
            (filter) =>
              !(Object.values(AWVStatusFilter) as string[]).includes(filter)
          );
        }

        const newFilters = [
          ...filteredExistingFilters.filter((f) => f !== "All"),
          toggledFilter,
        ];

        setSelectedFilters(newFilters);
        if (
          (Object.values(AWVStatusFilter) as string[]).includes(toggledFilter)
        ) {
          setSelectedValue(toggledFilter);
        }
        getHMOData(DEFAULT_PAGE_NUM, DEFAULT_PAGE_SIZE, false, newFilters);
      }
    }
  };

  // Function to render the button groups isFilterSelected("ACO Only")
  const renderButtonGroup = () => {
    return (
      <div style={{ display: "flex", gap: "5px" }}>
        <Button
          type={selectedFilters.length === 0 ? "primary" : "default"}
          onClick={() => handleFilterToggle("All")}
        >
          All
        </Button>
        <Button
          type={isFilterSelected("HMO Only") ? "primary" : "default"}
          onClick={() => handleFilterToggle("HMO Only")}
        >
          Medicare Advantage
        </Button>
        <Button
          type={isFilterSelected("ACO Only") ? "primary" : "default"}
          onClick={() => handleFilterToggle("ACO Only")}
        >
          Medicare ACO
        </Button>
      </div>
    );
  };

  const segregateMembersByType = (members: PatientRecord[]) => {
    const memberIds: string[] = [];
    const memberMbis: string[] = [];

    members.forEach((member: PatientRecord) => {
      if (member.MemberType === AWVMemberType.HMO) {
        memberIds.push(member.MemberId);
      } else {
        memberMbis.push(member.MemberMbi);
      }
    });

    return { memberIds, memberMbis };
  };

  const createPayload = (
    memberIds: string[],
    awvStatus: string,
    memberType: string
  ) => ({
    memberIds: memberIds,
    awvStatus: awvStatus,
    measureYear: moment().year(),
    memberType: memberType,
    userId: USERID!,
  });

  const processUpdate = (
    memberIds: string[],
    payload: any,
    successMessage: string
  ) => {
    if (memberIds.length > 0) {
      props
        .updateMemberAwvStatus(payload)
        .then(() => {
          notifier.success(successMessage);
          setFilterDataSource((prevData) =>
            prevData.map((item) => {
              if (item.MemberType === AWVMemberType.HMO) {
                // Filter by MemberId if member type is HMO
                return item.MemberId === item.MemberId
                  ? { ...item, AwvStatus: payload.awvStatus }
                  : item;
              } else {
                // Filter by MemberMbi for non-HMO members
                return item.MemberMbi === item.MemberMbi
                  ? { ...item, AwvStatus: payload.awvStatus }
                  : item;
              }
            })
          );
        })
        .catch((err: any) => {
          notifier.error(
            err.response.data.Message ||
              "Something went wrong, please contact a system administrator."
          );
        });
    }
  };

  // Function to handle batch status changes
  const batchStatusChange = (awvStatus: string) => {
    const { memberIds, memberMbis } = segregateMembersByType(selectedMembers);

    const hmoPayload = createPayload(memberIds, awvStatus, AWVMemberType.HMO);
    const acoPayload = createPayload(memberMbis, awvStatus, AWVMemberType.ACO);

    processUpdate(
      memberIds,
      hmoPayload,
      "HMO Member Status updated successfully."
    );
    processUpdate(
      memberMbis,
      acoPayload,
      "ACO Member Status updated successfully."
    );
  };

  // Function to handle batch status resets
  const batchResetStatusChange = () => {
    const { memberIds, memberMbis } = segregateMembersByType(selectedMembers);

    const hmoPayload = createPayload(
      memberIds,
      AWVStatus.Needed,
      AWVMemberType.HMO
    );
    const acoPayload = createPayload(
      memberMbis,
      AWVStatus.Needed,
      AWVMemberType.ACO
    );

    processUpdate(
      memberIds,
      hmoPayload,
      "HMO Member Status updated successfully."
    );
    processUpdate(
      memberMbis,
      acoPayload,
      "ACO Member Status updated successfully."
    );
  };

  const updateMemberAwvStatus = (
    record: PatientRecord,
    awvStatus: string
  ): void => {
    const payload = {
      memberIds:
        record.MemberType == AWVMemberType.HMO
          ? [record.MemberId]
          : [record.MemberMbi],
      awvStatus: awvStatus,
      measureYear: moment().year(),
      memberType: record.MemberType,
      userId: USERID!,
    };
  
    props
      .updateMemberAwvStatus(payload)
      .then(() => {
        notifier.success("Status updated successfully.");
        setFilterDataSource((prevData) =>
          prevData.map((item) => {
            if (record.MemberType === AWVMemberType.HMO) {
              // Filter by MemberId if member type is HMO
              return item.MemberId === record.MemberId
                ? { ...item, AwvStatus: awvStatus }
                : item;
            } else {
              // Filter by MemberMbi for non-HMO members
              return item.MemberMbi === record.MemberMbi
                ? { ...item, AwvStatus: awvStatus }
                : item;
            }
          })
        );
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const resetStatus = (record: PatientRecord): void => {
    const payload = {
      memberIds:
        record.MemberType == AWVMemberType.HMO
          ? [record.MemberId]
          : [record.MemberMbi],
      awvStatus: AWVStatus.Needed,
      measureYear: moment().year(),
      memberType: record.MemberType,
      userId: USERID!,
    };

    props
      .updateMemberAwvStatus(payload)
      .then(() => {
        notifier.success("Status updated successfully.");
        setFilterDataSource((prevData) =>
          prevData.map((item) => {
            if (record.MemberType === AWVMemberType.HMO) {
              // Filter by MemberId if member type is HMO
              return item.MemberId === record.MemberId
                ? { ...item, AwvStatus: AWVStatus.Needed }
                : item;
            } else {
              // Filter by MemberMbi for non-HMO members
              return item.MemberMbi === record.MemberMbi
                ? { ...item, AwvStatus: AWVStatus.Needed }
                : item;
            }
          })
        );
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const downloadAllMemberPafs = () => {
    let memberIds: string[] = [];
    selectedMembers.forEach((member: PatientRecord) => {
      memberIds.push(member.MemberId ? member.MemberId : member.MemberMbi);
    });

    const payload: MemberPafModel = {
      memberIds: memberIds,
      companyId: selectedIPA,
      memberType: "all",
    };

    props
      .downloadMemberPafs(payload)
      .then((documents: any) => {
        const blob = new Blob([documents], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const date = new Date().toISOString().slice(0, 10);
        const filename = `Members_PAF_${date}.pdf`;

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notifier.success("Member PAFs has been downloaded successfully.");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const downloadMemberPafs = (record: PatientRecord) => {
    let memberId =
      record.MemberType == AWVMemberType.HMO
        ? record.MemberId
        : record.MemberMbi;
    const payload: MemberPafModel = {
      memberIds: [memberId],
      companyId: selectedIPA,
      memberType: record.MemberType,
    };

    props
      .downloadMemberPafs(payload)
      .then((documents: any) => {
        const blob = new Blob([documents], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const date = new Date().toISOString().slice(0, 10);
        const filenamePrefix = memberId ? `Member-${memberId}` : "Members";
        const filename = `${filenamePrefix}_PAF_${date}.pdf`;

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notifier.success("Member PAFs has been downloaded successfully.");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const downloadAwvStatus = (fileType: string) => {
    const filtersWithoutSpaces = selectedFilters.includes("All")
      ? []
      : selectedFilters.map((filter) => filter.replace(/\s+/g, ""));

    const payload: MemberExcelStatusModel = {
      providerId: providerId,
      companyId: selectedIPA,
      memberType: AWVMemberType.HMO || "",
      measureYear: moment().year(),
      filter: filtersWithoutSpaces,
    };

    if (fileType === "xlsx") {
      props
        .downloadAwvStatusExcel(payload)
        .then((documents: any) => {
          if (!documents) {
            notifier.error("No documents received to download.");
            return;
          }
          const blob = new Blob([documents], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");
          const now = new Date();
          const dateString = `${now.getFullYear()}-${
            now.getMonth() + 1
          }-${now.getDate()}`;
          const fileName = `AWV_Status_${dateString}.xlsx`;

          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notifier.success(
            "Status Summary Excel file has been downloaded successfully."
          );
        })
        .catch((err: ErrorResponse) => {
          const errorMessage =
            err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator.";
          notifier.error(errorMessage);
        });
    }

    if (fileType === "pdf") {
      props
        .downloadAwvStatusPdf(payload)
        .then((documents: any) => {
          if (!documents) {
            notifier.error("No documents received to download.");
            return;
          }
          const blob = new Blob([documents], {
            type: "application/pdf",
          });
          const link = document.createElement("a");
          const now = new Date();
          const dateString = `${now.getFullYear()}-${
            now.getMonth() + 1
          }-${now.getDate()}`;
          const fileName = `AWV_Status_${dateString}.pdf`;

          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notifier.success(
            "Status Summary PDF file has been downloaded successfully."
          );
        })
        .catch((err: ErrorResponse) => {
          const errorMessage =
            err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator.";
          notifier.error(errorMessage);
        });
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], record: any) => {
      const memberIds = record.map((item: PatientRecord) => item.MemberId);
      setSelectedMemberIds(memberIds);
      setSelectedMembers(record);
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: PatientRecord) => {
      const today = new Date();
      const awvDueDate = record.AwvDueDate
        ? new Date(record.AwvDueDate)
        : undefined;
      return {
        disabled:
          record.AwvStatus === AWVStatus.Completed ||
          (record.AwvStatus === AWVStatus.Needed &&
            awvDueDate &&
            awvDueDate > today),
      };
    },
  };

  const columns = [
    {
      title: "PATIENT",
      dataIndex: "MemberName",
      key: "MemberName",
      width: '17%',
      render: (text: string, record: PatientRecord) => (
        <>
          <p style={{ fontWeight: "bold" }}>{record.MemberName}</p>
          <p>{`${record.MemberGender}, ${moment(record.MemberBirth).format(
            "MM/DD/YYYY"
          )}`}</p>
          <p>{formatPhoneNumber(record.MemberPhone)}</p>
        </>
      ),
    },
    {
      title: "INFO",
      dataIndex: "MemberDetail",
      key: "MemberDetail",
      width: '17%',
      render: (text: string, record: PatientRecord) => (
        <>
          <p
            style={{ fontWeight: "bold" }}
          >{`Open HCCs: ${record.MemberHccCount}`}</p>
          <p style={{ fontWeight: "bold" }}>{`HEDIS Gaps: ${
            record?.HedisGaps ? record.HedisGaps : "N/A"
          }`}</p>
          <p>{`${record.MemberType}`}</p>
        </>
      ),
    },
    {
      title: "LAST ANNUAL VISIT",
      dataIndex: "LastAwvDate",
      key: "LastAwvDate",
      width: '17%',
      render: (text: string, record: PatientRecord) => {
        if (!record.PreviousAwvRecords) {
          return "";
        }

        // Extract the first date in YYYY-MM-DD format from the PreviousAwvRecords string
        const dates = record.PreviousAwvRecords.match(/\b\d{4}-\d{2}-\d{2}\b/);
        const lastAwv = dates ? dates[0] : "N/A";

        // Calculate "months ago" based on today's date
        let monthAgoStr = "N/A";
        if (lastAwv !== "N/A") {
          const lastAwvDate = new Date(lastAwv);
          const today = new Date();
          const monthDiff =
            (today.getFullYear() - lastAwvDate.getFullYear()) * 12 +
            today.getMonth() -
            lastAwvDate.getMonth();
          // Construct the string for "months ago"
          monthAgoStr = `${monthDiff} month${monthDiff !== 1 ? "s" : ""} ago`;
        }
        return (
          <>
            <p style={{ fontWeight: "bold" }}>
              {lastAwv !== "N/A" ? `${monthAgoStr}` : ""}
            </p>
            <p>{lastAwv !== "N/A" ? `${dates}` : ""}</p>
          </>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "Status",
      key: "Status",
      width: '17%',
      render: (text: string, record: PatientRecord) => {
        const today = new Date();
        let backgroundColor = "";
        let status = record?.AwvStatus;

        if (status === AWVStatus.Needed) {
          if (!record.AwvDueDate || new Date(record.AwvDueDate) < today) {
            text = "Visit Needed";
            backgroundColor = "warning";
          } else {
            text = `Eligible on ${new Date(
              record.AwvDueDate
            ).toLocaleDateString()}`;
            backgroundColor = "default";
          }
        } else if (status === AWVStatus.Completed) {
          text = "Visit Completed";
          backgroundColor = "success";
        } else if (status === AWVStatus.Scheduled) {
          text = "Visit Scheduled";
          backgroundColor = "black";
        } else if (status === AWVStatus.Pending) {
          text = `Visit Done`;
          backgroundColor = "success";
        }
        return (
          <Tag
            color={backgroundColor}
            style={{
              fontSize: "16px",
              border: "0",
              padding: "5px 10px",
              borderRadius: "30px",
            }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "ACTIONS ",
      key: "Actions",
      width: '32%',
      render: (text: string, record: PatientRecord) => {
        const today = new Date();
        const awvDueDate = record.AwvDueDate
          ? new Date(record.AwvDueDate)
          : null;
        return (
          <Space>
            {/* PAF button available for all statuses */}
            <Button
              icon={<DownloadOutlined style={{ color: "#1890ff" }} />}
              onClick={() => downloadMemberPafs(record)}
              style={{
                color: "#1890ff",
                borderColor: "#1890ff",
                borderWidth: "1px",
              }}
            >
              PAF
            </Button>

            {/* Conditionally render other buttons based on status and AwvDueDate */}
            {record.AwvStatus === AWVStatus.Needed && (!awvDueDate || awvDueDate < today) && (
              <>
                <Button
                  onClick={() => updateMemberAwvStatus(record, AWVStatus.Scheduled)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Visit Scheduled
                </Button>
                <Button
                  onClick={() => updateMemberAwvStatus(record, AWVStatus.Pending)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Visit Done
                </Button>
              </>
            )}

            {record.AwvStatus === AWVStatus.Needed && awvDueDate && awvDueDate > today && (
              <Button icon={<CloseOutlined />} disabled>
                Not Yet Eligible
              </Button>
            )}

            {record.AwvStatus === AWVStatus.Scheduled && (
              <>
                <Button
                  onClick={() => updateMemberAwvStatus(record, AWVStatus.Pending)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Visit Done
                </Button>
                <Button
                  onClick={() => resetStatus(record)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Reset Status
                </Button>
              </>
            )}

            {record.AwvStatus === AWVStatus.Pending && (
              <>
                <Button
                  onClick={() => updateMemberAwvStatus(record, AWVStatus.Scheduled)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Visit Scheduled
                </Button>
                <Button
                  onClick={() => resetStatus(record)}
                  className={styles["action-button"]}
                  style={{ borderColor: "#1890ff", color: "#1890ff" }}
                >
                  Reset Status
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    //populate table upon component render
    getDistinctProviders();
  }, []);

  useEffect(() => {
    // Trigger refresh of member AWV status when both providerId and selectedIPA are set
    if (providerId && selectedIPA) {
      refreshMemberAwvStatus();
    }
  }, [providerId, selectedIPA]);

  const handleMemberNameSearch = (value: string) => {
    memberSearchText.current = value;
    getHMOData();
  };

  const getHMOData = async (
    page = DEFAULT_PAGE_NUM,
    pageSize = DEFAULT_PAGE_SIZE,
    isExport = false,
    filters = selectedFilters
  ) => {
    setIsLoading(true);
    const filtersWithoutSpaces = filters.includes("All")
      ? []
      : filters.map((filter) => filter.replace(/\s+/g, ""));
    const payload = {
      providerId,
      companyId: selectedIPA!,
      pageIndex: page,
      pageSize,
      measureYear: moment().year(),
      memberType: null,
      memberName: memberSearchText.current,
      filter: filtersWithoutSpaces,
    };

    setCurrentPage(page);
    setPageSize(pageSize);

    try {
      const response = await props.getMemberAwvDetails(payload);
      const hmoMember = response.map((member: any, index: any) => ({
        ...member,
        key: index,
      }));

      setFilterDataSource(hmoMember);
      setTotalEntries(response[0]?.TotalMemberCount ?? 0);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        "Something went wrong, please contact a system administrator.";
      notifier.error(errorMessage);
    } finally {
      setIsLoading(false);
      setSelectedRowKeys([]);
    }
  };

  const updateSelectedProvider = (provider: string) => {
    setSelectedProvider(provider);
    setProviderId(JSON.parse(provider).RmiProvId);
    setProviderKeyId(JSON.parse(provider).rmiProvKeyId);
    setSelectedFilters([]);
    setSelectedValue(undefined);
    memberSearchText.current = "";
    setSelectedMemberName("");
    setSelectedIPA(JSON.parse(provider).rmiCompanyId);
  };

  const getDistinctProviders = () => {
    return props
      .getDistinct(USERID!)
      .then((providers: any[]) => {
        const formattedProviders = formatName(providers);
        const pcps = formattedProviders.filter((prov: any) => {
          const isDesiredClass = ["D", "P", "3C", "C"].includes(prov.RmiClass);
          const hasAccessDays =
            prov.RmiClaimAccessDays === null ||
            parseInt(prov.RmiClaimAccessDays) > 0;
          return isDesiredClass && hasAccessDays;
        });

        if (pcps.length > 0) {
          const firstPcp = JSON.stringify(pcps[0]);
          updateSelectedProvider(firstPcp);
          setSelectedProvider(firstPcp);
          setSelectedIPA(pcps[0]?.rmiCompanyId);
          const providerList = sortArrayByKey(pcps, "Provider");
          setProviderList(providerList);
        }
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const HmoTableComponent: React.FC = () => {
    const getRowClassName = (record: PatientRecord) => {
      return record.AwvStatus === AWVStatus.Completed
        ? styles["row-completed"]
        : "";
    };

    return (
      <Table
        rowSelection={rowSelection}
        dataSource={filterDataSource}
        columns={columns}
        loading={
          props.isUpdatingHmoMetrics || props.isFetchingDistinct || isLoading
        }
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          position: ["bottomCenter"],
          hideOnSinglePage: true,
          pageSizeOptions: ["10", "25", "50"],
          showSizeChanger: true,
          defaultPageSize: DEFAULT_PAGE_SIZE,
          total: totalEntries,
          onChange(pageNum: number, pageSize?: number) {
            setCurrentPage(pageNum);
            getHMOData(pageNum, pageSize);
          },
        }}
        rowClassName={getRowClassName}
      />
    );
  };

  const refreshMemberAwvStatus = () => {
    setIsLoading(true);
    const payload: RefreshMemberAwvStatusModel = {
      providerId: providerId,
      companyId: selectedIPA!,
      measureYear: moment().year(),
      memberType: null,
      userId: USERID!,
      provKey: providerKeyId,
    };

    props
      .refreshMemberAwvStatus(payload)
      .then(() => {
        getHMOData();
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  return (
    <>
      <div className={styles["hmo-table"]}>
        <div style={{ width: "100%", marginBottom: "10px" }}>
          <div className="row" style={{ alignItems: "center" }}>
            <div
              className="col"
              style={{ display: "flex", justifyContent: "start" }}
            >
              {renderButtonGroup()}
            </div>
            <div
              className="col"
              style={{ display: "flex", justifyContent: "end" }}
            >
              <div
                className="select-container"
                style={{ marginBottom: "10px" }}
              >
                <Select
                  className="mt-2"
                  style={{ width: "300px", fontWeight: "bold" }}
                  showSearch
                  value={selectedProvider}
                  onSelect={(value) => updateSelectedProvider(value)}
                  loading={props.isFetchingDistinct}
                >
                  {providerList.map((option: any, index: any) => (
                    <Option key={index} value={JSON.stringify(option)}>{`${
                      option.Provider
                    } (${option.RmiCity ? option.RmiCity : "N/A"}${
                      option.RmiStreet ? ` ${option.RmiCity}` : ""
                    })`}</Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div style={{ width: "100%", marginBottom: "20px" }}>
          <div className="row" style={{ alignItems: "center" }}>
            <div
              className="col"
              style={{ display: "flex", justifyContent: "start" }}
            >
              <AutoComplete
                className={styles['left-align-input']}
                value={selectedMemberName}
                placeholder="Search Member Name"
                onSearch={debounce(handleMemberNameSearch, 500)}
                onChange={setSelectedMemberName} // Handle changes
                allowClear={true}
              >
                <Input suffix={<SearchOutlined />} />
              </AutoComplete>
              <div className={styles['filter-container']}>
                <span>Filter: </span>
                <span className={styles['separator']}>|</span>
                <Button
                  type="link"
                  onClick={() => handleFilterToggle(AWVStatusFilter.VisitNeeded)}
                  className={`${styles['filter-button']} ${selectedFilters.includes(AWVStatusFilter.VisitNeeded) ? styles['selected'] : ''}`}
                >
                  Visit Needed
                </Button>
                <span className={styles['separator']}>|</span>
                <Button
                  type="link"
                  onClick={() => handleFilterToggle(AWVStatusFilter.Scheduled)}
                  className={`${styles['filter-button']} ${selectedFilters.includes(AWVStatusFilter.Scheduled) ? styles['selected'] : ''}`}
                >
                  Visit Scheduled
                </Button>
                <span className={styles['separator']}>|</span>
                <Button
                  type="link"
                  onClick={() => handleFilterToggle(AWVStatusFilter.PerformedClaimPending)}
                  className={`${styles['filter-button']} ${selectedFilters.includes(AWVStatusFilter.PerformedClaimPending) ? styles['selected'] : ''}`}
                >
                  Visit Done
                </Button>
                <span className={styles['separator']}>|</span>
                <Button
                  type="link"
                  onClick={() => handleFilterToggle(AWVStatusFilter.Completed)}
                  className={`${styles['filter-button']} ${selectedFilters.includes(AWVStatusFilter.Completed) ? styles['selected'] : ''}`}
                >
                  Visit Confirmed
                </Button>
                <span className={styles['separator']}>|</span>
                <Button
                  type="link"
                  onClick={() => handleFilterToggle(AWVStatusFilter.NotYetEligible)}
                  className={`${styles['filter-button']} ${selectedFilters.includes(AWVStatusFilter.NotYetEligible) ? styles['selected'] : ''}`}
                >
                  Not Yet Eligible
                </Button>
              </div>
            </div>
            <div
              className="col"
              style={{ display: "flex", justifyContent: "end" }}
            >
              <Space className={styles["action-buttons"]}>
                {selectedRowKeys.length !== 0 ? (
                  <>
                    <div className={styles["total-patients"]}>
                      {selectedRowKeys.length} Patients Selected (x)
                    </div>
                    <Button
                      className={styles["button"]}
                      onClick={() => downloadAllMemberPafs()}
                    >
                      Download Selected
                    </Button>
                    <Button
                      className={styles["button"]}
                      onClick={() => batchStatusChange(AWVStatus.Pending)}
                    >
                      Mark As Performed
                    </Button>
                    <Button
                      className={styles["button"]}
                      onClick={() => batchStatusChange(AWVStatus.Scheduled)}
                    >
                      Mark As Scheduled
                    </Button>
                    <Button
                      className={styles["button"]}
                      onClick={() => batchResetStatusChange()}
                    >
                      Reset Status
                    </Button>
                  </>
                ) : (
                  <div className={styles["total-patients"]}>
                    {totalEntries} Patients in Total
                  </div>
                )}

                {!isLoading ? (
                  <Button
                    onClick={() => downloadAwvStatus("pdf")}
                    className={styles["export-table-btn"]}
                  >
                    Export List (pdf)
                  </Button>
                ) : null}
                {!isLoading ? (
                  <Button
                    onClick={() => downloadAwvStatus("xlsx")}
                    className={styles["export-table-btn"]}
                  >
                    Export List (Excel)
                  </Button>
                ) : null}
              </Space>
            </div>
          </div>
        </div>

        <div className={styles["table-container"]}>
          <HmoTableComponent />
        </div>
      </div>
    </>
  );
};

export default HmoTable;
